import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { MatSelectChange } from '@angular/material/select';
import { convertSecondsToPeriodAndUnit, getRatioForPeriod } from 'core/date/seconds-to-period-and-unit';
import { digitsOnly, isNilOrEmpty } from 'helpers';

/**
 * Displays input that allows user to select a relative time frame.
 * Output and input are in seconds.
 */
@Component({
  selector: 'app-relative-date-input',
  templateUrl: `./relative-date-input.component.html`,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RelativeDateInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => RelativeDateInputComponent), multi: true }
  ],
  styleUrls: ['./relative-date-input.component.scss']
})
export class RelativeDateInputComponent implements ControlValueAccessor, Validator {
  // Bound to units input
  units: any = null;
  // Bound to period select
  selectedPeriod: 'day' | 'week' | 'hour' = 'day';

  timePeriods = [{
    name: 'Days',
    value: 'day'
  },
  {
    name: 'Weeks',
    value: 'week'
  },
  {
    name: 'Hours',
    value: 'hour'
  }
  ];

  @Input() inputTheme: 'white' | 'gray' = 'gray';
  /** Disabled state of the input */
  @Input() disabled: boolean;

  // For ControlValueAccessor interface
  public onChangeCallback: () => void;
  public onTouchedCallback: () => any;

  constructor(private _cd: ChangeDetectorRef) {
  }

  onResetClick() {
    this.units = null;
    this.selectedPeriod = null;
    this.handleDataChange();
  }

  unitsChanged(input: any) {
    const cleanedDigits = digitsOnly(input);
    this.units = cleanedDigits;
    this.handleDataChange();
  }

  periodChanged(input: MatSelectChange) {
    this.selectedPeriod = input.value;
    this.handleDataChange();
  }

  beforeAfterRadioChanged(_input: MatRadioChange) {
    if (isNilOrEmpty(this.selectedPeriod)) {
      this.selectedPeriod = 'day';
    }
    this.handleDataChange();
  }

  // ----------Validator Implementation----------
  validate(control: AbstractControl): ValidationErrors | null {
    return null;
  }

  // ----------ControlValueAccessor Implementation----------
  @Input()
  get value(): number {
    return this.convertToSeconds();
  }

  set value(value: any) {
    if (!isNilOrEmpty(value)) {
      const valAsInt = parseInt(value, 10);
      const convertedInput = convertSecondsToPeriodAndUnit(valAsInt);
      this.selectedPeriod = convertedInput.period;
      this.units = Math.abs(convertedInput.units);
    } else {
      this.selectedPeriod = null;
      this.units = null;
    }

    this.handleDataChange();
  }

  writeValue(obj: any): void {
    if (obj) {
      this.value = obj;
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = () => fn(this.value);
  }
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = () => fn(this.value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this._cd.detectChanges();
  }

  // ----------END----------

  private convertToSeconds(): number {
    if (isNilOrEmpty(this.units) || isNilOrEmpty(this.selectedPeriod)) {
      return null;
    } else {
      const unitsNumber = parseInt(this.units, 10);
      const ratioForPeriod = getRatioForPeriod(this.selectedPeriod);
      const seconds = ratioForPeriod * unitsNumber;
      if (isNaN(seconds)) {
        return null;
      }
      return seconds * -1;
    }
  }

  private handleDataChange(): void {
    this._cd.markForCheck();
    if (this.onChangeCallback) {
      this.onChangeCallback();
    }
  }
}
